import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  User,
  IndianRupee,
  Calendar,
  Edit,
  Phone,
  Mail,
  MessageSquare,
  FileText,
  TrendingUp,
  Clock,
  Target,
  Loader2,
  AlertCircle,
  Trash2,
  CheckCircle2,
  Save,
  X,
  Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

import { toast } from '../../hooks/use-toast';

import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { dealsAPI, leadsAPI, usersAPI, plansAPI, demosAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { cn } from '../../lib/utils';
import PricingHistory from '../../components/deals/PricingHistory';

// All deal stages
const ALL_DEAL_STAGES = [
  { value: 'POST_DEMO_FOLLOW_UP', label: 'Post Demo Follow-up', color: 'bg-blue-100 text-blue-700' },
  { value: 'PROPOSAL_SHARED', label: 'Proposal Shared', color: 'bg-purple-100 text-purple-700' },
  { value: 'NEGOTIATION_IN_PROGRESS', label: 'Negotiation', color: 'bg-amber-100 text-amber-700' },
  { value: 'PROCUREMENT_APPROVAL_PENDING', label: 'Procurement Pending', color: 'bg-orange-100 text-orange-700' },
  { value: 'ON_HOLD_BAD_TIMING', label: 'On Hold', color: 'bg-slate-100 text-slate-700' },
  { value: 'PENDING_ADMIN_APPROVAL', label: 'Done (Request Approval)', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'WON_PAYMENT_PENDING', label: 'Won (Payment Pending)', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'WON_PAYMENT_RECEIVED', label: 'Won (Paid)', color: 'bg-green-100 text-green-700' },
  { value: 'IMPLEMENTATION_IN_PROGRESS', label: 'Implementation', color: 'bg-cyan-100 text-cyan-700' },
  { value: 'LIVE_ACTIVE', label: 'Live & Active', color: 'bg-green-100 text-green-700' },
  { value: 'CHURNED_EARLY', label: 'Churned Early', color: 'bg-red-100 text-red-700' },
  { value: 'LOST_PRICE', label: 'Lost - Price', color: 'bg-red-100 text-red-700' },
  { value: 'LOST_TO_COMPETITOR', label: 'Lost - Competitor', color: 'bg-red-100 text-red-700' },
  { value: 'LOST_NO_DECISION', label: 'Lost - No Decision', color: 'bg-gray-100 text-gray-700' },
];

const getStageInfo = (stage) => {
  return ALL_DEAL_STAGES.find(s => s.value === stage) || { 
    value: stage, 
    label: stage?.replace(/_/g, ' '), 
    color: 'bg-slate-100 text-slate-700'
  };
};

const DealDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  
  const isAdmin = currentUser?.role === 'ADMIN';
  const isManager = currentUser?.role === 'MANAGER' || currentUser?.role === 'SALES_MANAGER';
  const canDelete = isAdmin || isManager;
  const canApprove = isAdmin || isManager;
  const canEdit = isAdmin || isManager;

  const [deal, setDeal] = useState(null);
  const [lead, setLead] = useState(null);
  const [demo, setDemo] = useState(null);
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isMoveStageOpen, setIsMoveStageOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState('');
  const [editedDeal, setEditedDeal] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Fetch deal data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dealRes, usersRes, plansRes] = await Promise.all([
          dealsAPI.get(id),
          usersAPI.list(),
          plansAPI.list()
        ]);
        setDeal(dealRes.data);
        setEditedDeal(dealRes.data);
        setUsers(usersRes.data || []);
        setPlans(plansRes.data || []);
        
        // Get related lead
        if (dealRes.data?.lead_id) {
          try {
            const leadRes = await leadsAPI.get(dealRes.data.lead_id);
            setLead(leadRes.data);
          } catch {
            setLead(null);
          }
        }
        
        // Get related demo
        if (dealRes.data?.demo_id) {
          try {
            const demoRes = await demosAPI.get(dealRes.data.demo_id);
            setDemo(demoRes.data);
          } catch {
            setDemo(null);
          }
        }
      } catch (error) {
        console.error('Failed to fetch deal:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const getEmployeeName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unassigned';
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      const response = await dealsAPI.update(id, {
        deal_value_estimate: editedDeal.deal_value_estimate,
        close_probability: editedDeal.close_probability,
        expected_close_date: editedDeal.expected_close_date,
        total_users: editedDeal.total_users,
        notes: editedDeal.notes
      });
      setDeal({ ...deal, ...response.data });
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to save deal:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to save changes',
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMoveStage = async () => {
    try {
      await dealsAPI.update(id, { deal_stage: selectedStage });
      setDeal(prev => ({ ...prev, deal_stage: selectedStage }));
      setEditedDeal(prev => ({ ...prev, deal_stage: selectedStage }));
      setIsMoveStageOpen(false);
    } catch (error) {
      console.error('Failed to update stage:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to update stage',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleDelete = async () => {
    try {
      await dealsAPI.delete(id);
      navigate('/deals');
    } catch (error) {
      console.error('Failed to delete deal:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to delete deal',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  const handleApprove = async (approvalStage) => {
    try {
      await dealsAPI.approve(id, approvalStage);
      setDeal(prev => ({ ...prev, deal_stage: approvalStage, close_probability: 100 }));
      setApprovalDialogOpen(false);
    } catch (error) {
      console.error('Failed to approve deal:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to approve deal',
        description: error.response?.data?.detail || error.message
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        <span className="ml-2 text-slate-500">Loading deal...</span>
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <AlertCircle className="w-12 h-12 text-slate-400 mb-4" />
        <p className="text-lg text-slate-600">Deal not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/deals')}>
          Back to Deals
        </Button>
      </div>
    );
  }

  const stageInfo = getStageInfo(deal.deal_stage);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between flex-wrap gap-4">
        <div className="flex items-start gap-3 sm:items-center sm:gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/deals')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white break-words">{deal.company_name || 'Unknown Company'}</h1>
            <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 break-words">{deal.contact_name || 'No contact'} • {formatCurrency(deal.deal_value_estimate)}</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-wrap w-full sm:w-auto">
          <Badge className={cn('text-sm px-3 py-1', stageInfo.color)}>
            {stageInfo.label}
          </Badge>
          
          {deal.deal_stage === 'PENDING_ADMIN_APPROVAL' && canApprove && (
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => setApprovalDialogOpen(true)}
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Approve
            </Button>
          )}
          
          <Button variant="outline" onClick={() => setIsMoveStageOpen(true)}>
            <TrendingUp className="w-4 h-4 mr-2" />
            Move Stage
          </Button>
          
          {canEdit && (
            <Button variant="outline" onClick={() => setIsEditing(!isEditing)}>
              {isEditing ? <X className="w-4 h-4 mr-2" /> : <Edit className="w-4 h-4 mr-2" />}
              {isEditing ? 'Cancel' : 'Edit'}
            </Button>
          )}
          
          {canDelete && (
            <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={() => setDeleteDialogOpen(true)}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Left Column - Deal Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Deal Value Card */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-emerald-500" />
                Deal Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">Deal Value</p>
                  {isEditing ? (
                    <Input
                      type="number"
                      className="mt-1 text-lg font-bold"
                      value={editedDeal.deal_value_estimate || ''}
                      onChange={(e) => setEditedDeal({...editedDeal, deal_value_estimate: parseFloat(e.target.value)})}
                    />
                  ) : (
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                      {formatCurrency(deal.deal_value_estimate)}
                    </p>
                  )}
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Monthly Revenue</p>
                  {(() => {
                    // Source of truth: deal.mrr (gets updated by WEF revisions from Clients page)
                    const currentMrr = deal?.mrr || 0;

                    let oldMrr = null;
                    let wefDate = null;
                    if (deal?.pricing_revision_history) {
                      try {
                        const history = typeof deal.pricing_revision_history === 'string'
                          ? JSON.parse(deal.pricing_revision_history)
                          : deal.pricing_revision_history;
                        if (Array.isArray(history) && history.length > 0) {
                          const sorted = [...history].sort((a, b) => new Date(b.at || b.wef) - new Date(a.at || a.wef));
                          const lastPlanChange = sorted.find(h => h?.changes_summary?.plan_amount !== undefined && h?.changes_summary?.plan_amount !== null);
                          if (lastPlanChange) {
                            oldMrr = lastPlanChange?.old_values?.plan_amount ?? null;
                            wefDate = lastPlanChange?.wef ?? null;
                          }
                        }
                      } catch {
                        // ignore
                      }
                    }

                    return (
                      <div>
                        <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                          {formatCurrency(currentMrr)}/mo
                        </p>
                        {oldMrr !== null && oldMrr !== currentMrr && (
                          <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                            <span className="line-through">{formatCurrency(oldMrr)}/mo</span>
                            {wefDate ? (
                              <span className="ml-2">WEF {new Date(wefDate).toLocaleDateString('en-IN')}</span>
                            ) : null}
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Close Probability</p>
                  {isEditing ? (
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      className="mt-1 text-lg font-bold"
                      value={editedDeal.close_probability || ''}
                      onChange={(e) => setEditedDeal({...editedDeal, close_probability: parseInt(e.target.value)})}
                    />
                  ) : (
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-2 bg-purple-200 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-500 rounded-full" style={{ width: `${deal.close_probability || 0}%` }} />
                      </div>
                      <span className="text-xl font-bold text-purple-700 dark:text-purple-300">{deal.close_probability || 0}%</span>
                    </div>
                  )}
                </div>
                <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">Total Users</p>
                  {isEditing ? (
                    <Input
                      type="number"
                      className="mt-1 text-lg font-bold"
                      value={editedDeal.total_users || ''}
                      onChange={(e) => setEditedDeal({...editedDeal, total_users: parseInt(e.target.value)})}
                    />
                  ) : (
                    <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                      {deal.total_users || 0}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Details */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-blue-500" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                  <Building2 className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Company</p>
                    <p className="font-medium text-slate-800 dark:text-white">{deal.company_name || lead?.company_name || 'Unknown'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                  <User className="w-5 h-5 text-slate-500" />
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Contact</p>
                    <p className="font-medium text-slate-800 dark:text-white">{deal.contact_name || lead?.contact_name || 'Unknown'}</p>
                  </div>
                </div>
                {lead && (
                  <>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                      <Phone className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Phone</p>
                        <p className="font-medium text-slate-800 dark:text-white">{lead.contact_phone || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-700 rounded-lg">
                      <Mail className="w-5 h-5 text-slate-500" />
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Email</p>
                        <p className="font-medium text-slate-800 dark:text-white">{lead.contact_email || '-'}</p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                <Button variant="outline" size="sm" disabled={!lead?.contact_phone}>
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </Button>
                <Button variant="outline" size="sm" disabled={!lead?.contact_phone}>
                  <MessageSquare className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
                <Button variant="outline" size="sm" disabled={!lead?.contact_email}>
                  <Mail className="w-4 h-4 mr-2" />
                  Email
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Plan & Pricing Details */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-500" />
                Plan & Pricing Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Plan Name</p>
                  <p className="font-medium text-slate-800 dark:text-white">{deal.plan_name || '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Expected Close Date</p>
                  {isEditing ? (
                    <Input
                      type="date"
                      value={editedDeal.expected_close_date?.split('T')[0] || ''}
                      onChange={(e) => setEditedDeal({...editedDeal, expected_close_date: e.target.value})}
                    />
                  ) : (
                    <p className="font-medium text-slate-800 dark:text-white">
                      {deal.expected_close_date ? new Date(deal.expected_close_date).toLocaleDateString('en-IN') : '-'}
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Subtotal</p>
                  <p className="font-medium text-slate-800 dark:text-white">{formatCurrency(deal.subtotal)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">GST ({deal.gst_percentage || 18}%)</p>
                  <p className="font-medium text-slate-800 dark:text-white">{formatCurrency(deal.gst_amount)}</p>
                </div>
              </div>

              {/* Line Items with Extra User Bifurcation */}
              {deal.line_items && deal.line_items.length > 0 && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-slate-600">Line Items & Pricing Breakdown</p>
                    <span className="text-xs text-slate-500">(Line items may be historical; current MRR shown above)</span>
                  </div>

                  {/* Current Plan row (WEF) */}
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Current Monthly Plan (WEF)</span>
                      <span className="font-semibold text-blue-700 dark:text-blue-300">{formatCurrency(deal?.mrr || 0)}/mo</span>
                    </div>
                    {(() => {
                      let oldMrr = null;
                      let wefDate = null;
                      if (deal?.pricing_revision_history) {
                        try {
                          const history = typeof deal.pricing_revision_history === 'string'
                            ? JSON.parse(deal.pricing_revision_history)
                            : deal.pricing_revision_history;
                          if (Array.isArray(history) && history.length > 0) {
                            const sorted = [...history].sort((a, b) => new Date(b.at || b.wef) - new Date(a.at || a.wef));
                            const lastPlanChange = sorted.find(h => h?.changes_summary?.plan_amount !== undefined && h?.changes_summary?.plan_amount !== null);
                            if (lastPlanChange) {
                              oldMrr = lastPlanChange?.old_values?.plan_amount ?? null;
                              wefDate = lastPlanChange?.wef ?? null;
                            }
                          }
                        } catch {
                          // ignore
                        }
                      }

                      if (oldMrr === null || oldMrr === (deal?.mrr || 0)) return null;
                      return (
                        <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                          Old: <span className="line-through">{formatCurrency(oldMrr)}/mo</span>
                          {wefDate ? <span className="ml-2">WEF {new Date(wefDate).toLocaleDateString('en-IN')}</span> : null}
                        </p>
                      );
                    })()}
                  </div>

                  <div className="space-y-2">
                    {deal.line_items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 dark:bg-slate-700 rounded">
                        <span className="text-sm text-slate-600 dark:text-slate-300">{item.description}</span>
                        <span className="font-medium text-slate-800 dark:text-white">{formatCurrency(item.total_price)}</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Extra Users Bifurcation */}
                  {deal.total_users > 0 && deal.base_user_capacity > 0 && deal.total_users > deal.base_user_capacity && (
                    <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-2 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Extra Users Breakdown
                      </p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-amber-600">Base Users (Included):</span>
                          <span className="ml-1 font-medium">{deal.base_user_capacity}</span>
                        </div>
                        <div>
                          <span className="text-amber-600">Total Users:</span>
                          <span className="ml-1 font-medium">{deal.total_users}</span>
                        </div>
                        <div>
                          <span className="text-amber-600">Extra Users:</span>
                          <span className="ml-1 font-medium text-amber-800">{deal.total_users - deal.base_user_capacity}</span>
                        </div>
                        {deal.extra_user_rate > 0 && (
                          <div>
                            <span className="text-amber-600">Rate per Extra User:</span>
                            <span className="ml-1 font-medium">{formatCurrency(deal.extra_user_rate)}/mo</span>
                          </div>
                        )}
                      </div>
                      {deal.extra_user_rate > 0 && (
                        <div className="mt-2 pt-2 border-t border-amber-200 dark:border-amber-700">
                          <span className="text-xs text-amber-600">Extra Users Cost:</span>
                          <span className="ml-1 font-semibold text-amber-800">
                            {formatCurrency((deal.total_users - deal.base_user_capacity) * deal.extra_user_rate)}/mo
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {isEditing && (
                <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <Button variant="outline" onClick={() => { setIsEditing(false); setEditedDeal(deal); }}>
                    Cancel
                  </Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleSaveChanges} disabled={saving}>
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pricing History Section */}
          {deal.pricing_revision_history && (
            <PricingHistory historyJson={deal.pricing_revision_history} />
          )}
        </div>

        {/* Right Column - Summary */}
        <div className="space-y-6">
          {/* Weighted Value */}
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
            <CardContent className="p-5">
              <div className="text-center">
                <p className="text-sm text-emerald-100 mb-2">Weighted Value</p>
                <p className="text-3xl font-bold">
                  {formatCurrency((deal.deal_value_estimate || 0) * ((deal.close_probability || 0) / 100))}
                </p>
                <p className="text-xs text-emerald-200 mt-1">Value × Probability</p>
              </div>
            </CardContent>
          </Card>

          {/* Owner */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-500">Deal Owner</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-medium">
                  {getEmployeeName(deal.primary_employee_id).split(' ').map(n => n[0]).join('').slice(0,2)}
                </div>
                <div>
                  <p className="font-medium text-slate-800 dark:text-white">{getEmployeeName(deal.primary_employee_id)}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Sales Representative</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sales Manager */}
          {deal.sales_manager_id && (
            <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-500">Sales Manager</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-medium">
                    {getEmployeeName(deal.sales_manager_id).split(' ').map(n => n[0]).join('').slice(0,2)}
                  </div>
                  <div>
                    <p className="font-medium text-slate-800 dark:text-white">{getEmployeeName(deal.sales_manager_id)}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Manager</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Timeline */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-500">Timeline</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-slate-500 dark:text-slate-400">Created:</span>
                <span className="text-slate-800 dark:text-white">
                  {new Date(deal.created_at).toLocaleDateString('en-IN')}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-slate-400" />
                <span className="text-slate-500 dark:text-slate-400">Expected Close:</span>
                <span className="text-slate-800 dark:text-white">
                  {deal.expected_close_date ? new Date(deal.expected_close_date).toLocaleDateString('en-IN') : '-'}
                </span>
              </div>
              {deal.actual_close_date && (
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span className="text-slate-500 dark:text-slate-400">Closed:</span>
                  <span className="text-emerald-600">
                    {new Date(deal.actual_close_date).toLocaleDateString('en-IN')}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Related Records */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-500">Related Records</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {lead && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate(`/leads/${lead.id}`)}
                >
                  <FileText className="w-4 h-4 mr-2 text-blue-500" />
                  Lead: {lead.company_name}
                </Button>
              )}
              {demo && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => navigate(`/demos`)}
                >
                  <Target className="w-4 h-4 mr-2 text-purple-500" />
                  Demo: {demo.id?.slice(-8)}
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Move Stage Modal */}
      <Dialog open={isMoveStageOpen} onOpenChange={setIsMoveStageOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Move Deal Stage</DialogTitle>
            <DialogDescription>
              Select the new stage for this deal.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>New Stage</Label>
            <Select value={selectedStage} onValueChange={setSelectedStage}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select stage" />
              </SelectTrigger>
              <SelectContent>
                {ALL_DEAL_STAGES.map(stage => {
                  // Filter stages for employees
                  if (!isAdmin && !isManager) {
                    if (stage.value.includes('WON') || stage.value.includes('LIVE')) return null;
                  }
                  
                  return (
                    <SelectItem key={stage.value} value={stage.value}>
                      <div className="flex items-center gap-2">
                        <Badge className={cn('text-xs', stage.color)}>{stage.label}</Badge>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMoveStageOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleMoveStage}
              disabled={!selectedStage}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Move Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Deal
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this deal for <strong>{deal.company_name}</strong>? 
              This will also delete all associated incentive records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete Deal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approval Dialog */}
      <AlertDialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="w-5 h-5" />
              Approve Deal
            </AlertDialogTitle>
            <AlertDialogDescription>
              Approve this deal for <strong>{deal.company_name}</strong> ({formatCurrency(deal.deal_value_estimate)})?
              This will create incentive records and convert the deal to WON status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleApprove('WON_PAYMENT_PENDING')} 
              className="bg-amber-600 hover:bg-amber-700"
            >
              Payment Pending
            </AlertDialogAction>
            <AlertDialogAction 
              onClick={() => handleApprove('WON_PAYMENT_RECEIVED')} 
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              Payment Received
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DealDetailPage;
